import './App.css';
import { lazy, Suspense, useEffect, useState } from 'react';
import {
  HashRouter,
  Routes,
  Route,
  Navigate,
  useParams,
  useNavigate,
  useLocation,
} from 'react-router-dom';
import { Toaster } from 'sonner';
import SessionExpiredProvider from './context/SessionExpiredProvider';
import { ConfirmModalProvider } from './hooks/useConfirmModal';
import { FullscreenLoader } from './components/ui/SwotifyLoader';
import AppNavigationLoader from './components/layout/AppNavigationLoader';
import { dashboardPathForRole, ensureSession } from './lib/swotifyAuth';
import { isSystemPortal } from './lib/tenant';
import { trackPageView } from './lib/analytics';

/**
 * Client-side gate for the super-admin portal. Real enforcement is the backend
 * (every super-admin endpoint is require_role([SUPER_USER])); this just avoids
 * showing an empty/broken portal to a non-super session and bounces to login.
 */
function RequireSuperAdmin({ children }) {
  const role = (localStorage.getItem('swotify_role') || '').toLowerCase();
  if (role !== 'super_user') {
    return <Navigate to="/login" replace />;
  }
  return children;
}

const Login = lazy(() => import('./modules/auth/pages/login.jsx'));
const TermsPage = lazy(() => import('./modules/auth/pages/TermsPage.jsx'));
const PrivacyPage = lazy(() => import('./modules/auth/pages/PrivacyPage.jsx'));
const HelpCenterPage = lazy(() => import('./modules/auth/pages/HelpCenterPage.jsx'));
const StudentDashboard = lazy(() => import('./modules/student/pages/StudentDashboard.jsx'));
const TeacherDashboard = lazy(() => import('./modules/teacher/pages/TeacherDashboard.jsx'));
const StudentProfile = lazy(() => import('./modules/student/pages/StudentProfile.jsx'));
const AdminDashboard = lazy(() => import('./modules/admin/pages/AdminDashboard.jsx'));
const SuperAdminDashboard = lazy(() => import('./modules/superAdmin/pages/SuperAdminDashboard.jsx'));
const ParentStudent360Page = lazy(() => import('./modules/student360/pages/ParentStudent360Page.jsx'));
const GrowthStoryPage = lazy(() => import('./modules/student360/pages/GrowthStoryPage.jsx'));

function ParentRouteWrapper() {
  const { studentId } = useParams();
  const schoolCode = localStorage.getItem('swotify_school_code');
  return <ParentStudent360Page studentId={studentId} schoolCode={schoolCode} />;
}

function GrowthStoryRouteWrapper() {
  const { studentId } = useParams();
  const schoolCode = localStorage.getItem('swotify_school_code');
  return <GrowthStoryPage studentId={studentId} schoolCode={schoolCode} />;
}

/** Reopen / PWA start: restore session via refresh, then go to dashboard (not login). */
function BootRedirect() {
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = await ensureSession();
        if (cancelled) return;
        if (token) {
          const role = localStorage.getItem('swotify_role');
          // On the super-admin host, only a super session opens the portal;
          // anyone else is sent to the (system) login rather than their tenant.
          if (isSystemPortal() && String(role || '').toLowerCase() !== 'super_user') {
            navigate('/login', { replace: true });
            return;
          }
          navigate(dashboardPathForRole(role), { replace: true });
          return;
        }
      } catch {
        /* fall through to login */
      }
      if (!cancelled) navigate('/login', { replace: true });
    })().finally(() => {
      if (!cancelled) setChecking(false);
    });
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  return checking ? (
    <FullscreenLoader label="Restoring your session…" />
  ) : null;
}

/**
 * Fire a GTM `page_view` on every HashRouter route change. GTM's built-in
 * History listener does not pick up hash-route changes, so we push manually.
 */
function RouteAnalytics() {
  const location = useLocation();
  useEffect(() => {
    const path = `${location.pathname}${location.search}${location.hash}` || '/';
    trackPageView(path);
  }, [location.pathname, location.search, location.hash]);
  return null;
}

function AppShell() {
  return (
    <>
      <RouteAnalytics />
      <AppNavigationLoader />
      <Suspense fallback={<FullscreenLoader delayMs={0} label="Loading page…" />}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Navigate to="/login" replace />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/help" element={<HelpCenterPage />} />
          <Route path="/student-dashboard/*" element={<StudentDashboard />} />
          <Route path="/teacher-dashboard/*" element={<TeacherDashboard />} />
          <Route path="/admin-dashboard/*" element={<AdminDashboard />} />
          <Route
            path="/super-admin-dashboard/*"
            element={
              <RequireSuperAdmin>
                <SuperAdminDashboard />
              </RequireSuperAdmin>
            }
          />
          <Route path="/student/profile" element={<StudentProfile />} />
          <Route path="/parent/360/:studentId" element={<ParentRouteWrapper />} />
          <Route path="/student/:studentId/growth-story" element={<GrowthStoryRouteWrapper />} />
          <Route path="/" element={<BootRedirect />} />
        </Routes>
      </Suspense>
    </>
  );
}

function App() {
  return (
    <HashRouter
      future={{
        // Keep navigations outside startTransition so Suspense fallbacks
        // (fullscreen loader) actually appear while lazy pages load.
        v7_startTransition: false,
        v7_relativeSplatPath: true,
      }}
    >
      <SessionExpiredProvider>
        <ConfirmModalProvider>
          <AppShell />
          <Toaster position="top-center" richColors closeButton />
        </ConfirmModalProvider>
      </SessionExpiredProvider>
    </HashRouter>
  );
}

export default App;
